import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { env } from "@astro/config";
import type { Money } from "@astro/types";

/** Paid add-on picked for a cart line (stone weight, certificate, …) — one per
 * add-on group the admin set up on the product (`product.variations`). `optionId`
 * is what goes to the cart/checkout APIs (`variation_id`); the server prices the
 * line as total_price + each option's price, so `price` here is display-only. */
export interface CartItemOption {
  key: string;       // add-on group id (product_variations.id), e.g. "17"
  label: string;     // group display name, e.g. "Choose Stone Weight"
  value: string;     // selected option id as a string, e.g. "35"
  display: string;   // selected option label, e.g. "6.25 Ratti"
  price: number;     // add-on amount in paise (0 when included)
  optionId?: number; // product_variation_options.id sent upstream as variation_id
}

/** Server option ids for a line's add-ons — the `variation_id` the API expects. */
export const cartItemOptionIds = (item: CartItem) =>
  (item.options ?? []).map((o) => o.optionId).filter((id): id is number => typeof id === "number");

export interface CartItem {
  productId: string;
  title: string;
  price: Money; // base catalog price — option add-ons live in `options`
  qty: number;
  options?: CartItemOption[];
  /** Display-only catalog facts (main image url / category name). Basket
   * lines get these from the server cart at render time; express buy-now
   * items never touch the server cart, so they carry their own. */
  image?: string;
  category?: string;
}

/** Per-unit add-on total (paise) from an item's selected options. */
export const cartItemAddOns = (item: CartItem) =>
  item.options?.reduce((n, o) => n + o.price, 0) ?? 0;

interface CartState {
  items: CartItem[];
  /** Express-checkout ("buy now") line. Lives OUTSIDE `items` so an instant
   * purchase never mutates the saved basket. Checkout reads it when opened
   * in buy-now mode and clears it once the order completes. Persisted along
   * with the basket so it survives the login redirect. */
  buyNow: CartItem | null;
  add: (item: CartItem) => void;
  setQty: (productId: string, qty: number) => void;
  setOptions: (productId: string, options: CartItemOption[]) => void;
  remove: (productId: string) => void;
  clear: () => void;
  setBuyNow: (item: CartItem) => void;
  /** qty <= 0 clears the express line (mirrors setQty semantics). */
  setBuyNowQty: (qty: number) => void;
  clearBuyNow: () => void;
  count: () => number;
  subtotal: () => number;
}

/**
 * Cross-app cart. Persisted so a cart started in `shop` is visible when the
 * user hops to `jyotish` (same-origin storage; use a shared-storage bridge or
 * server-persisted cart for true cross-subdomain sync in production).
 */
export const useCartStore = create<CartState>()(
  // devtools wraps persist (outermost middleware) — the store shows up in the
  // Redux DevTools extension as "astro-cart" with one named action per mutation.
  devtools(
    persist(
      (set, get) => ({
        items: [],
        buyNow: null,
        add: (item) =>
          set(
            (s) => {
              const existing = s.items.find((i) => i.productId === item.productId);
              return existing
                ? {
                  items: s.items.map((i) =>
                    i.productId === item.productId ? { ...i, qty: i.qty + item.qty } : i
                  ),
                }
                : { items: [...s.items, item] };
            },
            false,
            "cart/add"
          ),
        setQty: (productId, qty) =>
          set(
            (s) => ({
              items:
                qty <= 0
                  ? s.items.filter((i) => i.productId !== productId)
                  : s.items.map((i) => (i.productId === productId ? { ...i, qty } : i)),
            }),
            false,
            "cart/setQty"
          ),
        setOptions: (productId, options) =>
          set(
            (s) => ({
              items: s.items.map((i) => (i.productId === productId ? { ...i, options } : i)),
            }),
            false,
            "cart/setOptions"
          ),
        remove: (productId) =>
          set(
            (s) => ({ items: s.items.filter((i) => i.productId !== productId) }),
            false,
            "cart/remove"
          ),
        clear: () => set({ items: [] }, false, "cart/clear"),
        setBuyNow: (item) => set({ buyNow: item }, false, "cart/setBuyNow"),
        setBuyNowQty: (qty) =>
          set(
            (s) => ({ buyNow: qty <= 0 ? null : s.buyNow && { ...s.buyNow, qty } }),
            false,
            "cart/setBuyNowQty"
          ),
        clearBuyNow: () => set({ buyNow: null }, false, "cart/clearBuyNow"),
        count: () => get().items.reduce((n, i) => n + i.qty, 0),
        subtotal: () =>
          get().items.reduce((n, i) => n + (i.price.amount + cartItemAddOns(i)) * i.qty, 0),
      }),
      {
        name: `astro-cart:${env.VITE_ENV}`,
        storage: createJSONStorage(() => localStorage),
        // v1: add-ons became real server options. Saved v0 carts carry the old
        // hardcoded picks (no optionId) that the server never charges — drop them
        // so the basket total can't show add-ons checkout won't bill.
        version: 1,
        migrate: (persisted, version) => {
          const state = persisted as Pick<CartState, "items" | "buyNow">;
          if (version >= 1 || !state) return state;
          const keepServerOptions = (item: CartItem | null) =>
            item && { ...item, options: item.options?.filter((o) => typeof o.optionId === "number") };
          return {
            ...state,
            items: (state.items ?? []).map((i) => keepServerOptions(i) as CartItem),
            buyNow: keepServerOptions(state.buyNow ?? null),
          };
        },
      }
    ),
    { name: "astro-cart", enabled: env.VITE_ENV === "development" }
  )
);

// UI store (client-only ephemeral state).
interface UiState {
  mobileNavOpen: boolean;
  setMobileNav: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  devtools(
    (set) => ({
      mobileNavOpen: false,
      setMobileNav: (open) => set({ mobileNavOpen: open }, false, "ui/setMobileNav"),
    }),
    { name: "astro-ui", enabled: env.VITE_ENV === "development" }
  )
);
